import { AccordionItemTitlePosition, OverlappedLogosIconSize, useFormatter, WithSkeleton } from '@capsicum/common';
import { Accordion, Divider, Image, OverlappedLogos, SkeletonItem, Text } from '@capsicum/common-web';
import { ChevronDownIcon } from '@capsicum/common-web/icons';
import { type WCPledgeBasketItemWithTeamData } from '@capsicum/features/world-championship-pledge/shared';
import styled, { useTheme } from 'styled-components';

type WCPledgeConfirmationSummaryProps = {
	basketDetailsList: Array<null | WCPledgeBasketItemWithTeamData>;
	formattedTotalPledgeAmount: string;
	teamCount: number;
};

const WCPledgeConfirmationSummaryComponent = ({
	basketDetailsList,
	formattedTotalPledgeAmount,
	teamCount
}: WCPledgeConfirmationSummaryProps) => {
	const { formatCrypto } = useFormatter();
	const { colors } = useTheme();

	const AccordionContent = () => {
		return (
			<>
				<S.BreakdownHeader>
					<Text color="main.unselected" variant="caption2">
						Breakdown
					</Text>
					<Divider orientation="horizontal" />
				</S.BreakdownHeader>
				<S.BreakdownListContainer>
					{basketDetailsList
						.filter(team => team !== null)
						.map(team => (
							<S.BreakdownRow key={team!.id}>
								<S.TeamInfo>
									<Image alt={team!.name} height={20} src={team.flagUrl} width={20} />
									<Text
										color="main.unselected"
										testID={`wcPledge-confirmation-summary-team-name-${team!.name}`}
										variant="text4"
									>
										{team!.name}
									</Text>
								</S.TeamInfo>
								<Text
									color="main.unselected"
									testID={`wcPledge-confirmation-summary-team-amount-${team!.name}`}
									variant="text4"
								>
									{formatCrypto(team!.amount, 'CHZ')}
								</Text>
							</S.BreakdownRow>
						))}
				</S.BreakdownListContainer>
			</>
		);
	};

	return (
		<S.SummaryCard>
			<S.SummaryRow>
				<S.TeamCountWrapper>
					<Text variant="text1">{teamCount}</Text>
					<Text variant="text3">Teams</Text>
				</S.TeamCountWrapper>
				<Text testID="wcPledge-confirmation-summary-total-amount" variant="title6">
					{formattedTotalPledgeAmount}
				</Text>
			</S.SummaryRow>
			<Accordion
				items={[
					{
						condensed: true,
						content: <AccordionContent />,
						icon: <ChevronDownIcon color={colors.icons.brand.enabled} height={16} width={16} />,
						id: '1',
						testID: 'wcPledge-confirmation-summary-accordion',
						title: (
							<OverlappedLogos
								iconSize={OverlappedLogosIconSize.SMALL}
								limitedListAmount={3}
								logos={basketDetailsList
									.filter(team => team !== null)
									.map(team => ({
										enabled: true,
										partnerName: team!.name,
										url: team!.flagUrl
									}))}
							/>
						),
						titlePosition: AccordionItemTitlePosition.RIGHT
					}
				]}
			/>
		</S.SummaryCard>
	);
};

const WCPledgeConfirmationSummarySkeleton = () => {
	return (
		<S.SummaryCard>
			<S.SummaryRow>
				<S.TeamCountWrapper>
					<SkeletonItem height={20} width={30} />
					<SkeletonItem height={16} width={50} />
				</S.TeamCountWrapper>
				<SkeletonItem height={24} width={120} />
			</S.SummaryRow>
			<S.SkeletonAccordionHeader>
				<S.SkeletonLogosContainer>
					<SkeletonItem height={24} width={24} />
					<SkeletonItem height={24} width={24} />
					<SkeletonItem height={24} width={24} />
				</S.SkeletonLogosContainer>
				<SkeletonItem height={16} width={16} />
			</S.SkeletonAccordionHeader>
		</S.SummaryCard>
	);
};

export const WCPledgeConfirmationSummary = WithSkeleton(
	WCPledgeConfirmationSummaryComponent,
	WCPledgeConfirmationSummarySkeleton
);

namespace S {
	export const SummaryCard = styled.div`
		display: flex;
		flex-direction: column;
		padding: ${({ theme }) => theme.spacings.medium};
		background-color: ${({ theme }) => theme.colors.backgrounds.main.container};
		border-radius: ${({ theme }) => theme.border.radius.medium['border-radius']};
		gap: ${({ theme }) => theme.spacings.xsmall};
	`;

	export const SummaryRow = styled.div`
		display: flex;
		justify-content: space-between;
		align-items: flex-start;
	`;

	export const TeamCountWrapper = styled.div`
		display: flex;
		align-items: center;
		gap: ${({ theme }) => theme.spacings.xxxsmall};
	`;

	export const BreakdownHeader = styled.div`
		display: flex;
		align-items: center;
		gap: ${({ theme }) => theme.spacings.xxsmall};
		padding-top: ${({ theme }) => theme.spacings.medium};
		padding-bottom: ${({ theme }) => theme.spacings.small};
	`;

	export const BreakdownListContainer = styled.div`
		display: flex;
		flex-direction: column;
		gap: ${({ theme }) => theme.spacings.small};
	`;

	export const BreakdownRow = styled.div`
		display: flex;
		justify-content: space-between;
		align-items: center;
	`;

	export const TeamInfo = styled.div`
		display: flex;
		align-items: center;
		column-gap: ${({ theme }) => theme.spacings.small};
	`;

	export const SkeletonAccordionHeader = styled.div`
		display: flex;
		justify-content: flex-end;
		align-items: center;
		gap: ${({ theme }) => theme.spacings.xxxsmall};
	`;

	export const SkeletonLogosContainer = styled.div`
		display: flex;
		gap: ${({ theme }) => theme.spacings.xxxsmall};
	`;
}
